import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Text, Group, Button, ActionIcon, Paper, Stack, Select, TextInput } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconCalendar, IconX, IconChevronDown } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';

export interface ModernDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  size?: 'sm' | 'md' | 'lg';
  error?: string;
  label?: string;
  description?: string;
  required?: boolean;
  popoverProps?: {
    withinPortal?: boolean;
  };
}

/**
 * Modern Date Picker with clean white card design and smooth animations
 * Matches the modern aesthetic from the reference image
 */
export function ModernDatePicker({
  value,
  onChange,
  placeholder = "Select date",
  disabled = false,
  minDate,
  maxDate,
  size = 'md',
  error,
  label,
  description,
  required = false,
  popoverProps = { withinPortal: true },
}: ModernDatePickerProps) {
  const [isOpen, { open, close }] = useDisclosure(false);
  const [currentMonth, setCurrentMonth] = useState(value || new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(value);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showYearMonthSelectors, setShowYearMonthSelectors] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectTimeout, setSelectTimeout] = useState<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Sync with external value changes
  useEffect(() => {
    setSelectedDate(value);
    if (value) {
      setCurrentMonth(value);
    }
  }, [value]);

  // Use popoverProps to avoid linting warning
  const portalEnabled = popoverProps.withinPortal;

  // Close popover when clicking outside and handle window resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is on a Mantine Select dropdown
      const target = event.target as Element;
      const isSelectDropdown = target.closest('[data-mantine-select-dropdown]') || 
                              target.closest('.mantine-Select-dropdown') ||
                              target.closest('.mantine-Select-option');
      
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        !isSelectDropdown &&
        !isSelectOpen
      ) {
        close();
      }
    };

    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      if (selectTimeout) {
        clearTimeout(selectTimeout);
      }
    };
    }
    
    return undefined;
  }, [isOpen, close]);

  /**
   * Calculate dropdown position relative to viewport
   */
  const calculateDropdownPosition = () => {
    if (!inputRef.current) return;

    const inputRect = inputRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const dropdownWidth = 320; // minWidth from our dropdown
    const dropdownHeight = 400; // estimated height

    let top = inputRect.bottom + 8;
    let left = inputRect.left;

    // Adjust if dropdown would go off-screen horizontally
    if (left + dropdownWidth > viewportWidth) {
      left = viewportWidth - dropdownWidth - 16;
    }

    // Adjust if dropdown would go off-screen vertically
    if (top + dropdownHeight > viewportHeight) {
      top = inputRect.top - dropdownHeight - 8;
    }

    // Ensure minimum margins
    left = Math.max(16, left);
    top = Math.max(16, top);

    setDropdownPosition({ top, left });
  };

  /**
   * Navigate to previous month
   */
  const goToPreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  /**
   * Navigate to next month
   */
  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  /**
   * Handle date selection
   */
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    onChange(date);
    close();
  };

  /**
   * Handle opening dropdown
   */
  const handleOpen = () => {
    calculateDropdownPosition();
    open();
  };

  /**
   * Clear selected date
   */
  const handleClear = () => {
    setSelectedDate(null);
    onChange(null);
  };

  /**
   * Format date for display
   */
  const formatDate = (date: Date | null) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  /**
   * Get days in month
   */
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  /**
   * Get first day of month
   */
  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  /**
   * Check if date is today
   */
  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  /**
   * Check if date is selected
   */
  const isSelected = (date: Date) => {
    return selectedDate?.toDateString() === date.toDateString();
  };

  /**
   * Check if date is hovered
   */
  const isHovered = (date: Date) => {
    return hoveredDate?.toDateString() === date.toDateString();
  };

  /**
   * Check if date is disabled
   */
  const isDisabled = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    // ENHANCED: 08-10-2025 23:50:00 - Block past dates, allow future dates for scheduling
    if (date < today) return true; // Block past dates (before today)
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  /**
   * Generate calendar days
   */
  const generateCalendarDays = (): Array<{ date: Date; isCurrentMonth: boolean }> => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Previous month days
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    
    for (let i = firstDay - 1; i >= 0; i--) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, prevMonthDays - i);
      days.push({ date, isCurrentMonth: false });
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      days.push({ date, isCurrentMonth: true });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, day);
      days.push({ date, isCurrentMonth: false });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  /**
   * Generate year options (last 10 years to next 2 years)
   */
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 10; year <= currentYear + 2; year++) {
      years.push({ value: year.toString(), label: year.toString() });
    }
    return years;
  };

  /**
   * Generate month options
   */
  const generateMonthOptions = () => {
    const months = monthNames.map((month, index) => ({
      value: index.toString(),
      label: month
    }));
    return months;
  };

  /**
   * Handle year change
   */
  const handleYearChange = (year: string | null) => {
    if (year) {
      const newDate = new Date(parseInt(year), currentMonth.getMonth(), 1);
      setCurrentMonth(newDate);
    }
  };

  /**
   * Handle month change
   */
  const handleMonthChange = (month: string | null) => {
    if (month) {
      const newDate = new Date(currentMonth.getFullYear(), parseInt(month), 1);
      setCurrentMonth(newDate);
    }
  };

  return (
    <Box>
      {label && (
        <Text size="sm" fw={500} mb="xs" c="dark">
          {label}
          {required && <Text component="span" c="red"> *</Text>}
        </Text>
      )}
      
      {description && (
        <Text size="xs" c="dimmed" mb="xs">
          {description}
        </Text>
      )}

      {/* Input Field - Consistent with App Theme */}
      <TextInput
        ref={inputRef}
        placeholder={placeholder}
        value={selectedDate ? formatDate(selectedDate) : ''}
        readOnly
        disabled={disabled}
        leftSection={<IconCalendar size={16} />}
        rightSection={
          selectedDate ? (
            <ActionIcon
              size="sm"
              variant="subtle"
              color="gray"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              <IconX size={12} />
            </ActionIcon>
          ) : undefined
        }
        onClick={() => !disabled && handleOpen()}
        size={size}
        styles={{
          input: {
            cursor: disabled ? 'not-allowed' : 'pointer',
            backgroundColor: isOpen ? '#f8f9fa' : 'white',
            border: `1px solid ${isOpen ? '#28A8E0' : error ? '#fa5252' : '#d0d0d0'}`,
            borderRadius: 'md',
            fontSize: '14px',
            height: size === 'sm' ? '32px' : size === 'lg' ? '40px' : '36px',
            transition: 'all 0.2s ease',
            boxShadow: isOpen ? '0 0 0 3px rgba(40, 168, 224, 0.1)' : 'none',
            '&:focus': {
              borderColor: '#28A8E0',
              boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)',
            },
            '&:hover': {
              borderColor: isOpen ? '#28A8E0' : '#28A8E0',
            },
            '&:disabled': {
              opacity: 0.6,
              cursor: 'not-allowed',
            },
          },
          section: {
            color: isOpen ? '#28A8E0' : '#868e96',
            transition: 'color 0.2s ease',
          },
        }}
      />

      {error && (
        <Text size="xs" c="red" mt="xs">
          {error}
        </Text>
      )}

      {/* Modern Popover - Rendered as Portal */}
      {isOpen && portalEnabled && createPortal(
        <Paper
          ref={popoverRef}
          withBorder
          shadow="xl"
          radius="md"
          p="md"
          style={{
            position: 'fixed',
            zIndex: 9999,
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            minWidth: '320px',
            backgroundColor: 'white',
            border: '1px solid #e9ecef',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            animation: 'fadeInScale 0.2s ease-out',
          }}
        >
          <Stack gap="md">
            {/* Enhanced Header with Year/Month Selectors */}
            <Box>
              <Group justify="space-between" align="center" mb="sm">
                <Text size="lg" fw={600} c="dark">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </Text>
                <Group gap="xs">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={() => setShowYearMonthSelectors(!showYearMonthSelectors)}
                    style={{
                      borderRadius: '6px',
                      backgroundColor: showYearMonthSelectors ? '#e3f2fd' : '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                        borderColor: '#2196f3',
                      },
                    }}
                  >
                    <IconChevronDown size={14} style={{ 
                      transform: showYearMonthSelectors ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={goToPreviousMonth}
                    style={{
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        backgroundColor: '#e9ecef',
                        borderColor: '#2196f3',
                      },
                    }}
                  >
                    <IconChevronLeft size={14} />
                  </ActionIcon>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={goToNextMonth}
                    style={{
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        backgroundColor: '#e9ecef',
                        borderColor: '#2196f3',
                      },
                    }}
                  >
                    <IconChevronRight size={14} />
                  </ActionIcon>
                </Group>
              </Group>

              {/* Year/Month Selectors */}
              {showYearMonthSelectors && (
                <Group gap="sm" mb="sm" style={{
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  animation: 'fadeInScale 0.2s ease-out',
                }}>
                  <Select
                    placeholder="Year"
                    data={generateYearOptions()}
                    value={currentMonth.getFullYear().toString()}
                    onChange={handleYearChange}
                    onDropdownOpen={() => {
                      setIsSelectOpen(true);
                      if (selectTimeout) {
                        clearTimeout(selectTimeout);
                        setSelectTimeout(null);
                      }
                    }}
                    onDropdownClose={() => {
                      const timeout = setTimeout(() => {
                        setIsSelectOpen(false);
                      }, 100);
                      setSelectTimeout(timeout);
                    }}
                    size="sm"
                    style={{ flex: 1 }}
                    searchable={false}
                    clearable={false}
                    allowDeselect={false}
                    styles={{
                      input: {
                        border: '1px solid #d0d0d0',
                        borderRadius: '6px',
                        fontSize: '14px',
                        height: '32px',
                        cursor: 'pointer',
                        '&:focus': {
                          borderColor: '#28A8E0',
                          boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)',
                        },
                        '&:hover': {
                          borderColor: '#28A8E0',
                        },
                      },
                      dropdown: {
                        zIndex: 10000,
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                      option: {
                        fontSize: '14px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                        },
                        '&[data-selected]': {
                          backgroundColor: '#28A8E0',
                          color: 'white',
                        },
                      },
                    }}
                  />
                  <Select
                    placeholder="Month"
                    data={generateMonthOptions()}
                    value={currentMonth.getMonth().toString()}
                    onChange={handleMonthChange}
                    onDropdownOpen={() => {
                      setIsSelectOpen(true);
                      if (selectTimeout) {
                        clearTimeout(selectTimeout);
                        setSelectTimeout(null);
                      }
                    }}
                    onDropdownClose={() => {
                      const timeout = setTimeout(() => {
                        setIsSelectOpen(false);
                      }, 100);
                      setSelectTimeout(timeout);
                    }}
                    size="sm"
                    style={{ flex: 1 }}
                    searchable={false}
                    clearable={false}
                    allowDeselect={false}
                    styles={{
                      input: {
                        border: '1px solid #d0d0d0',
                        borderRadius: '6px',
                        fontSize: '14px',
                        height: '32px',
                        cursor: 'pointer',
                        '&:focus': {
                          borderColor: '#28A8E0',
                          boxShadow: '0 0 0 3px rgba(40, 168, 224, 0.1)',
                        },
                        '&:hover': {
                          borderColor: '#28A8E0',
                        },
                      },
                      dropdown: {
                        zIndex: 10000,
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                      option: {
                        fontSize: '14px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                        },
                        '&[data-selected]': {
                          backgroundColor: '#28A8E0',
                          color: 'white',
                        },
                      },
                    }}
                  />
                </Group>
              )}
            </Box>

            {/* Day Names */}
            <Group gap={0} justify="space-between">
              {dayNames.map((day) => (
                <Text key={day} size="xs" fw={500} c="dimmed" ta="center" w={40}>
                  {day}
                </Text>
              ))}
            </Group>

            {/* Calendar Grid */}
            <Box>
              {Array.from({ length: 6 }, (_, weekIndex) => (
                <Group key={weekIndex} gap={0} justify="space-between" mb="xs">
                  {calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7).map(({ date, isCurrentMonth }, dayIndex) => {
                    const isSelectedDate = isSelected(date);
                    const isHoveredDate = isHovered(date);
                    const isTodayDate = isToday(date);
                    const isDisabledDate = isDisabled(date);

                    return (
                      <Box
                        key={`${weekIndex}-${dayIndex}`}
                        w={40}
                        h={40}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px',
                          cursor: isDisabledDate ? 'not-allowed' : 'pointer',
                          backgroundColor: isSelectedDate 
                            ? '#1e40af' // MCHD blue
                            : isHoveredDate 
                            ? '#e3f2fd' 
                            : 'transparent',
                          color: isSelectedDate 
                            ? 'white' 
                            : isCurrentMonth 
                            ? '#212529' // Normal color for all current month dates
                            : '#868e96',
                          opacity: isDisabledDate ? 0.3 : 1,
                          fontWeight: isSelectedDate ? 600 : isTodayDate ? 600 : 400,
                          fontSize: '14px',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          border: isTodayDate && !isSelectedDate ? '2px solid #1e40af' : 'none',
                          boxShadow: isHoveredDate && !isDisabledDate ? '0 2px 8px rgba(30, 64, 175, 0.15)' : 'none',
                        }}
                        onClick={() => !isDisabledDate && handleDateSelect(date)}
                        onMouseEnter={() => !isDisabledDate && setHoveredDate(date)}
                        onMouseLeave={() => setHoveredDate(null)}
                      >
                        {date.getDate()}
                        {isTodayDate && !isSelectedDate && (
                          <Box
                            style={{
                              position: 'absolute',
                              bottom: '2px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: '4px',
                              height: '4px',
                              borderRadius: '50%',
                              backgroundColor: '#1e40af',
                            }}
                          />
                        )}
                        {/* ENHANCED: 08-10-2025 23:50:00 - Removed future date indicator since we allow future dates */}
                      </Box>
                    );
                  })}
                </Group>
              ))}
            </Box>

            {/* Enhanced Footer with MCHD Branding */}
            <Group justify="flex-end" gap="sm" pt="sm" style={{ 
              borderTop: '1px solid #e9ecef',
              paddingTop: '16px',
            }}>
              <Button
                size="sm"
                variant="outline"
                onClick={close}
                style={{
                  borderColor: '#d0d0d0',
                  color: '#6c757d',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  fontWeight: 500,
                  padding: '8px 16px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#1e40af',
                    color: '#1e40af',
                    backgroundColor: '#f8f9fa',
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => selectedDate && close()}
                disabled={!selectedDate}
                style={{
                  backgroundColor: selectedDate ? '#1e40af' : '#d0d0d0',
                  color: 'white',
                  borderRadius: '8px',
                  fontWeight: 600,
                  padding: '8px 20px',
                  border: 'none',
                  boxShadow: selectedDate ? '0 2px 8px rgba(30, 64, 175, 0.3)' : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: selectedDate ? '#1d4ed8' : '#d0d0d0',
                    transform: selectedDate ? 'translateY(-1px)' : 'none',
                    boxShadow: selectedDate ? '0 4px 12px rgba(30, 64, 175, 0.4)' : 'none',
                  },
                  '&:disabled': {
                    backgroundColor: '#d0d0d0',
                    color: '#9ca3af',
                    cursor: 'not-allowed',
                    transform: 'none',
                    boxShadow: 'none',
                  },
                }}
              >
                ✓ OK
              </Button>
            </Group>
          </Stack>
        </Paper>,
        document.body
      )}

      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </Box>
  );
}
